/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, FileText, Download } from 'lucide-react';
import { Button } from '../../ui/Button';

interface GenerateReportModalProps {
    onClose: () => void;
    onSuccess: () => void;
}

export const GenerateReportModal: React.FC<GenerateReportModalProps> = ({ onClose, onSuccess }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [reportConfig, setReportConfig] = useState({
        dateRange: '30',
        includeSummary: true,
        includeTxVolume: true,
        includeClientActivity: false,
        includeFullLog: false,
        format: 'pdf'
    });
    
    const handleGenerate = async () => {
        setIsGenerating(true);
        // Simulate report generation
        await new Promise(res => setTimeout(res, 2500));
        setIsGenerating(false);
        onSuccess();
        onClose();
    };
    
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setReportConfig(prev => ({ ...prev, [name]: checked }));
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 10 }}
                className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-lg relative border border-stone-200 dark:border-[#2D3142]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-stone-100 dark:border-white/5 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <FileText size={20} className="text-nobel-gold" />
                        <h2 className="font-serif text-xl text-stone-900 dark:text-white">Generate Analytics Report</h2>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>
                
                <div className="p-6 space-y-6">
                    {/* Date Range */}
                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Date Range</label>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                           {['7', '30', '90'].map(range => (
                               <button 
                                 key={range}
                                 onClick={() => setReportConfig(prev => ({...prev, dateRange: range}))}
                                 className={`py-2 rounded-lg text-sm font-bold border transition-colors ${reportConfig.dateRange === range ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black border-transparent' : 'bg-stone-100 dark:bg-white/5 border-stone-200 dark:border-white/10 hover:border-stone-300'}`}
                               >
                                  Last {range} Days
                               </button>
                           ))}
                        </div>
                    </div>

                    {/* Report Content */}
                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Include in Report</label>
                        <div className="mt-2 space-y-3">
                           <label className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 cursor-pointer">
                               <input type="checkbox" name="includeSummary" checked={reportConfig.includeSummary} onChange={handleCheckboxChange} className="h-4 w-4 rounded accent-nobel-gold"/>
                               <span className="text-sm font-medium text-stone-800 dark:text-gray-200">Revenue Summary</span>
                           </label>
                           <label className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 cursor-pointer">
                               <input type="checkbox" name="includeTxVolume" checked={reportConfig.includeTxVolume} onChange={handleCheckboxChange} className="h-4 w-4 rounded accent-nobel-gold"/>
                               <span className="text-sm font-medium text-stone-800 dark:text-gray-200">Transaction Volume</span>
                           </label>
                           <label className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 cursor-pointer">
                               <input type="checkbox" name="includeFullLog" checked={reportConfig.includeFullLog} onChange={handleCheckboxChange} className="h-4 w-4 rounded accent-nobel-gold"/>
                               <span className="text-sm font-medium text-stone-800 dark:text-gray-200">Full Transaction Log</span>
                           </label>
                        </div>
                    </div>
                    
                    {/* Format */}
                    <div>
                         <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">File Format</label>
                         <div className="flex gap-3 mt-2">
                             <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${reportConfig.format === 'pdf' ? 'border-nobel-gold bg-nobel-gold/5' : 'border-stone-200 dark:border-white/10 hover:border-stone-300'}`}>
                                 <input type="radio" name="format" value="pdf" checked={reportConfig.format === 'pdf'} onChange={(e) => setReportConfig(prev => ({...prev, format: e.target.value}))} className="hidden"/>
                                 <span className="font-bold text-sm">PDF</span>
                             </label>
                             <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${reportConfig.format === 'csv' ? 'border-nobel-gold bg-nobel-gold/5' : 'border-stone-200 dark:border-white/10 hover:border-stone-300'}`}>
                                 <input type="radio" name="format" value="csv" checked={reportConfig.format === 'csv'} onChange={(e) => setReportConfig(prev => ({...prev, format: e.target.value}))} className="hidden"/>
                                 <span className="font-bold text-sm">CSV</span>
                             </label>
                         </div>
                    </div>

                </div>

                <div className="p-6 bg-stone-50 dark:bg-[#0A0B0F] border-t border-stone-100 dark:border-white/5 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleGenerate} isLoading={isGenerating}>
                        {isGenerating ? 'Generating...' : <><Download size={16} className="mr-2"/> Generate & Download</>}
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
};